import type { ComponentType, ReactNode } from 'react';
import { z, type ZodType } from 'zod';
import type { EntityField } from '@resource-kit/controllers/react';
import type { EnumOption, FieldType } from '@resource-kit/model';
import { failHard, type Formatters, type Translate } from '@resource-kit/core';
import { useResourceKit } from '@resource-kit/core/react';
import {
  calendarDayIn,
  resolveEnumItems,
  tzOffsetMsAt,
  type ValidatableField,
} from '@resource-kit/controllers';
/**
 * Straight from the package, not through the host's contracts shell (#2057).
 * This module and that one used to import each other: the contracts
 * derived `FieldType` from `FIELD_TYPE_REGISTRY` below, while this file took
 * the widget contract back from the contracts. The cycle died when `FieldType`
 * became a declared union in `@resource-kit/model`; naming the package here is
 * the other half of that, and it is now a plain host → package edge.
 */
import type {
  DataType,
  ModelField,
  TranslateFn,
  Widget,
  WidgetControl,
  WidgetProps,
} from '@resource-kit/model';
import { lookupEnumLabel } from '../../enum-label';
import { enumBadgeVariant } from '../../badge-variant';
import { Badge } from '../primitives/badge';
import { JsonKeyValueTable } from '../primitives/JsonKeyValueTable';
import { IdCell } from '../primitives/IdCell';
import { Input } from '../primitives/Input';
import { DatePicker } from '../primitives/DatePicker';
import { Textarea } from '../primitives/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/Select';
import { Check, ImageOff, X } from 'lucide-react';

/**
 * FIELD_TYPE_REGISTRY — the single closed-map source of truth for every
 * `FieldType`'s five projections (#1406):
 *
 *   1. `Cell`      — the list/detail grid component.
 *   2. `csvText`   — the plain-text CSV export projection.
 *   3. `Input`     — the create/edit form control component.
 *   4. `filterType`— the QueryBuilder DataType the column filters on.
 *   5. `zodBase`   — the default form-validation schema derived from the type.
 *
 * Before this map the five projections lived in five parallel `switch`
 * statements across `schema-utils.tsx` and `ResourceFormDialog.tsx`, each
 * needing manual sync (`schema-utils` even carried "keep in sync" warnings).
 * Adding a field type meant editing five places; forgetting one silently
 * produced a wrong render / export / filter. Now a field type is ONE row and
 * the `satisfies Record<FieldType, FieldTypeSpec>` below makes a missing row a
 * compile error — no runtime fallback.
 *
 * WHICH SIDE IS THE AUTHORITY REVERSED IN #2057. `FieldType` is now DECLARED as
 * a union in `@resource-kit/model`, where the schema algebra needs it, and this
 * map is checked AGAINST it: a type declared there with no row here fails the
 * `satisfies` below, and a row here for a type the package does not declare
 * fails as an excess property. It used to be the reverse — the union was
 * derived from this map's keys — which made a package's vocabulary depend on
 * one of its consumers. The check is the same strength either way; only its
 * direction changed.
 *
 * The `Cell` and `csvText` projections are byte-for-byte the previous
 * behavior; only `currency.Input` changed (a real currency control — see that
 * entry).
 *
 * Since #1805 the rendering itself lives in the WIDGET components at the foot
 * of this file, and `Cell` / `Input` are the legacy-shaped adapters onto them.
 * The map is still the authority — `FIELD_TYPE_WIDGETS` is derived from it, so
 * a field type cannot have a cell and no widget, or two different cells.
 */

export type ColumnMode = 'list' | 'detail';

// CSV projections only ever need a default-value fallback, so this narrow
// signature keeps the injected translator trivially stubbable in tests. It is
// declared in `@resource-kit/model` since #2057 — `WidgetCsvContext` is part of
// the widget contract and has to name it — and forwarded here, where every
// existing consumer already imports it from.
export type { TranslateFn };

// Form placeholders interpolate values (e.g. `{ field: fieldLabel }`), so the
// form projections take the live i18next translator, typed broadly enough for
// arbitrary interpolation options.
export type FormTranslateFn = (key: string, opts?: Record<string, unknown>) => string;

// ─── Value coercion helpers ────────────────────────────────
// Cell/CSV projections receive `unknown` (the value flows from the generic
// resource row through TanStack `getValue()` / an optional transform). These
// narrow it to what each projection needs while preserving the prior untyped
// runtime behavior (falsy fallbacks, `String()` coercion).
type CellPrimitive = string | number | boolean | null | undefined;

const asPrimitive = (value: unknown): CellPrimitive =>
  value === null ||
  value === undefined ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'
    ? value
    : String(value);

const asDateInput = (value: unknown): string | number | Date | null | undefined =>
  value === null ||
  value === undefined ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  value instanceof Date
    ? value
    : String(value);

const asIdValue = (value: unknown): string | number | null | undefined =>
  value === null || value === undefined || typeof value === 'string' || typeof value === 'number'
    ? value
    : String(value);

const parseJsonValue = (value: unknown): { parsed: unknown; error: boolean } => {
  if (value === null || value === undefined || value === '') return { parsed: null, error: false };
  try {
    return { parsed: typeof value === 'string' ? JSON.parse(value) : value, error: false };
  } catch {
    return { parsed: null, error: true };
  }
};

const stringifyRaw = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

// ─── Enum badge core (#1291) ───────────────────────────────
// Shared enum→Badge resolution for the grid cell and the detail-header status
// slot. `BadgeVariant` and `enumBadgeVariant` sit at the package ROOT since
// #2169 PR-2: their other consumers only NAME a variant, and naming one should
// not mean evaluating React.

/**
 * Shared enum→Badge core (#1291). Single resolution path for both the
 * list/detail grid cell and the detail-header status slot: resolve the option
 * for its `color`, look up the localized label FAIL-LOUD via
 * `lookupEnumLabelOrWarn` (visible `[unknown:*]` + console.warn on wire-form
 * drift, EQD §3 no-silent-fallback), and emit the deterministic
 * `enum-badge-<value>` testid so e2e specs target it the same way regardless of
 * surface. Callers differ only in how an empty/absent value renders, passed as
 * `emptyOutput` (header → `null`; grid cell → `'-'`).
 */
function renderEnumBadge(
  value: unknown,
  options: readonly EnumOption[],
  emptyOutput: ReactNode,
  kind: string,
  t: Translate,
): ReactNode {
  if (value === null || value === undefined || value === '') return emptyOutput;
  const raw = String(value);
  const option = options.find((o) => String(o.value) === raw);
  const label = lookupEnumLabel(raw, options, kind, t);
  return (
    <Badge variant={enumBadgeVariant(option?.color)} data-testid={`enum-badge-${raw}`}>
      {label}
    </Badge>
  );
}

/**
 * Render a status Badge from an `enum`-typed schema field + a wire value
 * (#1291). Used by the detail-header status-badge slot; an empty/absent value
 * renders nothing (vs the grid cell's `-`). Thin wrapper over `renderEnumBadge`.
 */
export function renderStatusBadge<T>(
  field: EntityField<T>,
  value: unknown,
  t: Translate,
): ReactNode {
  return renderEnumBadge(
    value,
    resolveEnumItems(field),
    null,
    t(field.label, { defaultValue: field.label }),
    t,
  );
}

// ─── JSON cell renderer ────────────────────────────────────
function renderJsonCell(
  value: unknown,
  mode: ColumnMode,
  t: Translate,
  ariaLabel?: string,
): ReactNode {
  const { parsed, error } = parseJsonValue(value);
  if (error)
    return <span className="text-xs font-mono text-destructive">{t('rk.schema.invalidJson')}</span>;

  if (mode === 'list') {
    if (parsed === null || parsed === undefined)
      return <span className="text-muted-foreground">-</span>;
    if (Array.isArray(parsed)) {
      return (
        <span className="text-xs text-muted-foreground">
          {parsed.length ? t('rk.schema.items', { count: parsed.length }) : '-'}
        </span>
      );
    }
    if (typeof parsed === 'object') {
      const keys = Object.keys(parsed);
      return (
        <span className="text-xs text-muted-foreground">
          {keys.length ? t('rk.schema.keys', { count: keys.length }) : '-'}
        </span>
      );
    }
    return <span className="text-xs text-muted-foreground">{String(parsed)}</span>;
  }

  return <JsonKeyValueTable value={parsed} ariaLabel={ariaLabel} />;
}

// ─── Form JSON validation ──────────────────────────────────
function isJsonFormValue(value: unknown): boolean {
  if (typeof value !== 'string') return z.json().safeParse(value).success;

  try {
    return z.json().safeParse(JSON.parse(value) as unknown).success;
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;
    return false;
  }
}

// ─── Projection contexts ───────────────────────────────────
export interface CellContext<T> {
  value: unknown;
  mode: ColumnMode;
  field: EntityField<T>;
}

export interface CsvContext<T> {
  value: unknown;
  field: EntityField<T>;
  translate: TranslateFn;
  /**
   * The host's formatters (#2169 PR-2). Three rows read it — `date` and
   * `datetime`, which used to call `@lib/utils` directly, and `currency`, which
   * used to build its own number formatter (#2184) — and it is the WHOLE
   * `Formatters` seam rather than the three members they happen to use: a
   * narrowed copy would be a second declaration of the same capability, and the
   * caller already holds the seam object anyway.
   */
  format: Formatters;
}

export interface FormInputContext<T> {
  field: EntityField<T>;
  value: unknown;
  onChange: (value: unknown) => void;
  readOnly?: boolean;
  fieldId: string;
  fieldLabel: string;
  t: FormTranslateFn;
  /** Field carries an issue — lands on the control as `aria-invalid` (#1797). */
  invalid?: boolean;
  /** Id of the node holding the issue text, for `aria-describedby` (#1797). */
  describedBy?: string;
  /** Fires when the control loses focus — drives blur-time validation (#1797). */
  onBlur?: () => void;
}

// ─── Widget props (#1805) ──────────────────────────────────
/**
 * The three presentation members `ModelField` deliberately does NOT carry.
 *
 * Capability-parity manifest §2 maps `placeholder` and `currencyCode` onto
 * named widgets rather than onto the model, so the v2 field contract has no
 * slot for them — but the type defaults below ARE the extraction of the legacy
 * renderers, which read all three, and dropping them would change what an
 * operator sees. They are declared OPTIONAL, which is exactly true (every
 * legacy field may omit them) and is also what keeps a
 * `ComponentType<TypeWidgetProps>` assignable to `ComponentType<WidgetProps>`:
 * a caller holding only a `ModelField` may leave them out, and every
 * projection here already copes with their absence.
 */
interface LegacyPresentation {
  placeholder?: string;
  description?: string;
  currencyCode?: string;
}

/** Props a type-default display projection receives. */
export type TypeWidgetProps = Omit<WidgetProps, 'field'> & {
  field: ModelField & LegacyPresentation;
};

/** Props a type-default input projection receives — `onChange` is mandatory. */
export type TypeWidgetInputProps = TypeWidgetProps & Required<Pick<TypeWidgetProps, 'onChange'>>;

/**
 * Legacy `EntityField` → the field a widget receives.
 *
 * `options` is the ONE member whose shape genuinely differs between the two
 * contracts (`StaticOptions` vs a flat `EnumOption[]`), so it is resolved here,
 * once, through the same `resolveEnumItems` the legacy renderers used. Every
 * other member rides along on the spread: `ModelField`'s own keys because they
 * are identical, the three `LegacyPresentation` ones because the widgets below
 * read them.
 *
 * `readonly` is the one member that is dropped rather than resolved. The two
 * contracts disagree on its type — a `FieldModifier` (possibly a predicate)
 * here, the model's plain boolean there — and no widget may read either: how a
 * control learns it is frozen is `control.readOnly`, already resolved against
 * the record and the in-flight save.
 */
export function toWidgetField<T>(field: EntityField<T>): ModelField & LegacyPresentation {
  const { readonly: _readonly, ...rest } = field;
  return { ...rest, options: resolveEnumItems(field) };
}

function displayProps<T>({ value, mode, field }: CellContext<T>): TypeWidgetProps {
  return { value, record: null, field: toWidgetField(field), mode: 'display', surface: mode };
}

function inputProps<T>(ctx: FormInputContext<T>): TypeWidgetInputProps {
  return {
    value: ctx.value,
    record: null,
    field: toWidgetField(ctx.field),
    mode: 'input',
    onChange: ctx.onChange,
    control: {
      id: ctx.fieldId,
      label: ctx.fieldLabel,
      readOnly: ctx.readOnly,
      invalid: ctx.invalid,
      describedBy: ctx.describedBy,
      onBlur: ctx.onBlur,
    },
  };
}

/**
 * A form control with no `WidgetControl` has no id to be labelled by, no
 * read-only state and no way to announce a validation issue. That is an
 * authoring mistake at the mount site, not something to paper over with
 * defaults — a control that silently loses its `aria-invalid` looks correct.
 */
function requireControl(control: WidgetControl | undefined): WidgetControl {
  if (control !== undefined) return control;
  return failHard(
    'a default input widget was mounted without a control contract; the id, label and a11y state belong to the field slot and cannot be derived',
  );
}

/** The a11y/interaction attributes every form control forwards verbatim (#1797). */
function controlAria(control: WidgetControl) {
  return {
    'aria-invalid': control.invalid ? true : undefined,
    'aria-describedby': control.describedBy,
    onBlur: control.onBlur,
  };
}

/**
 * The five projections a single field type defines. `satisfies
 * Record<FieldType, FieldTypeSpec>` on the map below turns a missing type into
 * a compile error (closed-map completeness — no runtime fallback).
 *
 * `Cell` and `Input` are COMPONENTS, not callbacks (#1799). They are mounted
 * as JSX by their consumers, so React owns their fibers and a projection may
 * legally call hooks — the services seam instead of the i18n singleton, a query
 * for a lookup label, local state for an inline editor. Before this they were
 * invoked directly, which made every such need a special case (`enum` already
 * had to smuggle in a nested `EnumSelect` component to reach a translator).
 * `Input`'s `onChange` is required: an input projection with nowhere to send
 * the edit is not an input.
 */
export interface FieldTypeSpec {
  /** list/detail grid cell — a component. */
  Cell<T>(ctx: CellContext<T>): ReactNode;
  /** plain-text CSV export projection. */
  csvText<T>(ctx: CsvContext<T>): string;
  /** create/edit form control — a component. */
  Input<T>(ctx: FormInputContext<T>): ReactNode;
  /** QueryBuilder filter DataType. */
  filterType: DataType;
  /**
   * default form-validation schema derived from the type (undefined = none).
   *
   * The parameter is the structural `ValidatableField` rather than
   * `EntityField<T>` (#2167 PR-3): the caller is
   * `@resource-kit/controllers`' `validateFieldValue`, which reaches this row
   * through the seed the host installs in `src/features/widgets.ts`, and a
   * package may not name a React contract. Narrowing costs nothing — of the
   * thirteen rows only `enum` reads the field at all, and it reads exactly the
   * two members `resolveEnumItems` takes, which `ValidatableField` carries.
   * Every `EntityField` still satisfies it, so no other call site changed.
   */
  zodBase(field: ValidatableField): ZodType<unknown> | undefined;
}

// ─── Display widgets ───────────────────────────────────────
// One component per field type; `FieldTypeSpec.Cell` below is the legacy-shaped
// adapter onto the same component, so there is exactly one implementation of
// each cell and the two entry points cannot drift.

function TextDisplay({ value }: TypeWidgetProps) {
  return <span className="text-sm text-foreground">{asPrimitive(value) || '-'}</span>;
}

function NumberDisplay({ value }: TypeWidgetProps) {
  return <span className="text-sm font-medium text-foreground">{asPrimitive(value) ?? 0}</span>;
}

function CurrencyDisplay({ value, field }: TypeWidgetProps) {
  // ABOVE the empty-value return, not beside the call that uses it: a hook
  // placed after an early return runs on some renders and not others, which is
  // the rules-of-hooks violation React throws on. `DateDisplay` and
  // `DateTimeDisplay` next door read the same seam from their first line and
  // have no early return to be caught by, so neither is a template for this one.
  const { format } = useResourceKit();
  if (value === null || value === undefined) return '-';
  const amount =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? parseFloat(value)
        : Number(value);
  return (
    <span className="font-medium text-foreground">
      {format.formatCurrency(amount, field.currencyCode ?? 'USD')}
    </span>
  );
}

function DateDisplay({ value }: TypeWidgetProps) {
  const { format } = useResourceKit();
  return (
    <span className="text-sm text-muted-foreground">{format.formatDate(asDateInput(value))}</span>
  );
}

function DateTimeDisplay({ value }: TypeWidgetProps) {
  const { format } = useResourceKit();
  return (
    <span className="text-sm text-muted-foreground">
      {value ? format.formatDateTimeCompact(asDateInput(value)) : '-'}
    </span>
  );
}

function BooleanDisplay({ value }: TypeWidgetProps) {
  const { t } = useResourceKit();
  // `relative` keeps the absolute sr-only span's containing block
  // inside the cell so it cannot stretch the document (#1253).
  return (
    <div className="relative flex items-center">
      {value ? (
        <Check className="h-4 w-4 text-success" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/30" />
      )}
      <span className="sr-only">{value ? t('rk.ui.yes') : t('rk.ui.no')}</span>
    </div>
  );
}

function EmailDisplay({ value }: TypeWidgetProps) {
  return (
    <div
      className="truncate text-sm max-w-[200px] font-medium text-foreground"
      title={typeof value === 'string' ? value : undefined}
    >
      {asPrimitive(value) || '-'}
    </div>
  );
}

function PhoneDisplay({ value }: TypeWidgetProps) {
  const text = asPrimitive(value);
  // An empty or blank value is a plain dash: a `tel:` link on it would open the
  // dialler on nothing (#2694). Any other value, `0` included, is a value.
  if (text === null || text === undefined || String(text).trim() === '') {
    return <span className="text-sm text-muted-foreground">-</span>;
  }
  return (
    <a
      href={`tel:${text}`}
      // The list row opens its detail on click; dialling must not also do that.
      onClick={(e) => e.stopPropagation()}
      className="text-sm text-muted-foreground hover:text-primary hover:underline"
    >
      {text}
    </a>
  );
}

function EnumDisplay({ value, field }: TypeWidgetProps) {
  const { t } = useResourceKit();
  return renderEnumBadge(
    value,
    field.options ?? [],
    '-',
    t(field.label, { defaultValue: field.label }),
    t,
  );
}

function ImageDisplay({ value }: TypeWidgetProps) {
  if (!value)
    return (
      <div className="w-8 h-8 bg-muted rounded flex items-center justify-center border border-border">
        <ImageOff className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
    );
  return (
    <img
      src={String(value)}
      alt="Thumbnail"
      className="w-8 h-8 object-cover rounded border border-border"
    />
  );
}

function JsonDisplay({ value, field, surface }: TypeWidgetProps) {
  const { t } = useResourceKit();
  return renderJsonCell(value, surface ?? 'list', t, t(field.label, { defaultValue: field.label }));
}

function ArrayDisplay({ value }: TypeWidgetProps) {
  const { t } = useResourceKit();
  return (
    <span className="text-sm text-muted-foreground">
      {Array.isArray(value) ? t('rk.schema.items', { count: value.length }) : '-'}
    </span>
  );
}

// List rows truncate to 12 chars (matches the prior visual density); detail
// rows shorten UUIDs only and leave every other shape whole (`'uuid'` — see
// `IdBudget`). Detail used to pass `false`, which printed a 36-character UUID
// inline while the list row two clicks away printed its tail; the shape
// dispatch is what keeps Shopify / Onfleet keys readable against those
// systems' own UIs. The full id is always reachable via the IdCell's hover
// tooltip + copy button, regardless of surface.
function IdDisplay({ value, surface }: TypeWidgetProps) {
  return (
    <IdCell value={asIdValue(value)} truncate={(surface ?? 'list') === 'list' ? 12 : 'uuid'} />
  );
}

// ─── Input widgets ─────────────────────────────────────────

// text / email / phone / image / id share the plain-text form control; only the
// input `type` attribute differs, so the component is built by a factory rather
// than taking an extra prop the `Widget` contract has no room for.
function plainTextInputWidget(inputType: string): ComponentType<TypeWidgetInputProps> {
  return function PlainTextInput({ value, field, onChange, control }: TypeWidgetInputProps) {
    const { t } = useResourceKit();
    const resolved = requireControl(control);
    return (
      <Input
        id={resolved.id}
        type={inputType}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || t('rk.resource.enterField', { field: resolved.label })}
        required={field.required}
        readOnly={resolved.readOnly}
        {...controlAria(resolved)}
      />
    );
  };
}

const TextInput = plainTextInputWidget('text');
const EmailInput = plainTextInputWidget('email');
const PhoneInput = plainTextInputWidget('tel');

function NumberInput({ value, field, onChange, control }: TypeWidgetInputProps) {
  const resolved = requireControl(control);
  return (
    <Input
      id={resolved.id}
      type="number"
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      placeholder={field.placeholder}
      required={field.required}
      readOnly={resolved.readOnly}
      {...controlAria(resolved)}
    />
  );
}

// #1406 behavior change: a real currency control (previously currency degraded
// to a bare `type="number"` Input that submitted a JS number, dropping
// precision and the currency semantics). Now: a numeric input that PRESENTS the
// field's `currencyCode` as a prefix affordance and SUBMITS the raw decimal
// STRING verbatim (`onChange(e.target.value)`), matching the backend
// decimal-string money convention (ADR-0011).
function CurrencyInput({ value, field, onChange, control }: TypeWidgetInputProps) {
  const resolved = requireControl(control);
  const code = field.currencyCode || 'USD';
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-muted-foreground">
        {code}
      </span>
      <Input
        id={resolved.id}
        type="number"
        inputMode="decimal"
        step="0.01"
        className="pl-14"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
        readOnly={resolved.readOnly}
        {...controlAria(resolved)}
      />
    </div>
  );
}

// date / datetime share the DatePicker; only `withTime` differs.
/** An instant names its zone: a trailing `Z` or `±HH:mm` (#2875). */
const ZONED_INSTANT = /(?:Z|[+-]\d{2}:\d{2})$/;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The value the picker shows: a record's INSTANT (`…Z`) becomes the wall clock
 * it reads in `timeZone` — `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` with a time —
 * so the control shows the merchant's day and time, not the UTC half of the
 * string (#2875). Anything else (the wall-clock strings the picker itself
 * emits, an empty value) passes through: a zone-less string is not an
 * instant, and parsing it would read it in the HOST zone.
 */
function wallClockOf(value: unknown, timeZone: string, withTime: boolean): string {
  const text = String(value ?? '');
  if (!ZONED_INSTANT.test(text)) return text;
  const at = new Date(text);
  if (Number.isNaN(at.getTime())) return text;
  const day = calendarDayIn(timeZone, at);
  if (!withTime) return day;
  // The zone's reading of the instant, held in a Date's UTC fields.
  const wall = new Date(at.getTime() + tzOffsetMsAt(timeZone, at));
  return `${day}T${pad2(wall.getUTCHours())}:${pad2(wall.getUTCMinutes())}`;
}

function dateInputWidget(withTime: boolean): ComponentType<TypeWidgetInputProps> {
  return function DateInput({ value, onChange, control }: TypeWidgetInputProps) {
    const resolved = requireControl(control);
    const { format } = useResourceKit();
    // Emits `YYYY-MM-DD` / `YYYY-MM-DDTHH:mm` — the same wire strings the
    // native date/datetime-local inputs produced, so everything downstream
    // of the form value sees no format change; an instant handed in is shown
    // as the merchant wall clock (`wallClockOf`). `field.required` is not
    // browser-enforced here
    // (the trigger is a button, not a form control); no resource currently
    // exposes a required date form field — revisit if one appears.
    return (
      <DatePicker
        id={resolved.id}
        data-testid={resolved.id}
        value={wallClockOf(value, format.timeZone, withTime)}
        withTime={withTime}
        onChange={onChange}
        disabled={resolved.readOnly}
        aria-invalid={resolved.invalid ? true : undefined}
        aria-describedby={resolved.describedBy}
        onBlur={resolved.onBlur}
      />
    );
  };
}

const DateInput = dateInputWidget(false);
const DateTimeInput = dateInputWidget(true);

function BooleanInput({ value, field, onChange, control }: TypeWidgetInputProps) {
  const resolved = requireControl(control);
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        id={resolved.id}
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={resolved.readOnly}
        className="rounded"
        {...controlAria(resolved)}
      />
      <span className="text-sm">{field.description || resolved.label}</span>
    </label>
  );
}

// A closed enum renders a Select; an enum with no resolvable options degrades
// to a free-text Input (some enum-typed fields have runtime-only option sets).
function EnumInput({ value, field, onChange, control }: TypeWidgetInputProps) {
  const items = field.options ?? [];
  const { t } = useResourceKit();
  const resolved = requireControl(control);
  const aria = controlAria(resolved);

  if (items.length === 0) {
    return (
      <Input
        id={resolved.id}
        type="text"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || t('rk.resource.enterField', { field: resolved.label })}
        readOnly={resolved.readOnly}
        {...aria}
      />
    );
  }

  return (
    <Select
      value={String(value ?? '')}
      onValueChange={(v) => onChange(v)}
      disabled={resolved.readOnly}
      // `items` feeds Select.Value the label; without it the trigger
      // shows the raw stored value until the popup has mounted once.
      items={Object.fromEntries(
        items.map((opt) => [String(opt.value), t(opt.label, { defaultValue: opt.label })]),
      )}
    >
      <SelectTrigger id={resolved.id} {...aria}>
        <SelectValue
          placeholder={field.placeholder || t('rk.resource.selectField', { field: resolved.label })}
        />
      </SelectTrigger>
      <SelectContent>
        {items.map((opt) => (
          <SelectItem key={String(opt.value)} value={String(opt.value)}>
            {t(opt.label, { defaultValue: opt.label })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// json / array share the JSON Textarea widget.
function JsonInput({ value, field, onChange, control }: TypeWidgetInputProps) {
  const resolved = requireControl(control);
  return (
    <Textarea
      id={resolved.id}
      rows={4}
      value={
        typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value, null, 2)
      }
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder || 'JSON'}
      readOnly={resolved.readOnly}
      className="font-mono text-xs"
      {...controlAria(resolved)}
    />
  );
}

// ─── Shared projection fragments ───────────────────────────
// text / email / phone share the CSV text projection (empty → dash).
function plainTextCsv({ value }: { value: unknown }): string {
  const p = asPrimitive(value);
  return p === null || p === undefined || p === '' ? '-' : String(p);
}

// ─── The registry ──────────────────────────────────────────
export const FIELD_TYPE_REGISTRY = {
  text: {
    Cell<T>(ctx: CellContext<T>) {
      return <TextDisplay {...displayProps(ctx)} />;
    },
    csvText: plainTextCsv,
    Input<T>(ctx: FormInputContext<T>) {
      return <TextInput {...inputProps(ctx)} />;
    },
    filterType: 'string',
    zodBase: () => undefined,
  },

  number: {
    Cell<T>(ctx: CellContext<T>) {
      return <NumberDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value }: CsvContext<T>) {
      return String(asPrimitive(value) ?? 0);
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <NumberInput {...inputProps(ctx)} />;
    },
    filterType: 'number',
    zodBase: () =>
      z.number({ error: 'rk.resource.validation.invalidNumber' }).finite({
        error: 'rk.resource.validation.invalidNumber',
      }),
  },

  currency: {
    Cell<T>(ctx: CellContext<T>) {
      return <CurrencyDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value, field, format }: CsvContext<T>) {
      if (value === null || value === undefined) return '-';
      const amount =
        typeof value === 'number'
          ? value
          : typeof value === 'string'
            ? parseFloat(value)
            : Number(value);
      return format.formatCurrency(amount, field.currencyCode || 'USD');
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <CurrencyInput {...inputProps(ctx)} />;
    },
    filterType: 'number',
    zodBase: () => undefined,
  },

  date: {
    Cell<T>(ctx: CellContext<T>) {
      return <DateDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value, format }: CsvContext<T>) {
      return format.formatDate(asDateInput(value));
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <DateInput {...inputProps(ctx)} />;
    },
    filterType: 'date',
    zodBase: () => z.iso.date({ error: 'rk.resource.validation.invalidDate' }),
  },

  datetime: {
    Cell<T>(ctx: CellContext<T>) {
      return <DateTimeDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value, format }: CsvContext<T>) {
      return value ? format.formatDateTimeCompact(asDateInput(value)) : '-';
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <DateTimeInput {...inputProps(ctx)} />;
    },
    filterType: 'datetime',
    zodBase: () => undefined,
  },

  boolean: {
    Cell<T>(ctx: CellContext<T>) {
      return <BooleanDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value, translate }: CsvContext<T>) {
      return value ? translate('rk.ui.yes') : translate('rk.ui.no');
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <BooleanInput {...inputProps(ctx)} />;
    },
    filterType: 'boolean',
    zodBase: () => undefined,
  },

  email: {
    Cell<T>(ctx: CellContext<T>) {
      return <EmailDisplay {...displayProps(ctx)} />;
    },
    csvText: plainTextCsv,
    Input<T>(ctx: FormInputContext<T>) {
      return <EmailInput {...inputProps(ctx)} />;
    },
    filterType: 'string',
    zodBase: () => undefined,
  },

  phone: {
    Cell<T>(ctx: CellContext<T>) {
      return <PhoneDisplay {...displayProps(ctx)} />;
    },
    csvText: plainTextCsv,
    Input<T>(ctx: FormInputContext<T>) {
      return <PhoneInput {...inputProps(ctx)} />;
    },
    filterType: 'string',
    zodBase: () => undefined,
  },

  enum: {
    Cell<T>(ctx: CellContext<T>) {
      return <EnumDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value, field, translate }: CsvContext<T>) {
      if (!value) return '-';
      const options = resolveEnumItems(field);
      const option = options?.find((o) => o.value === value);
      const rawLabel = option?.label || String(value);
      return translate(rawLabel, { defaultValue: rawLabel });
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <EnumInput {...inputProps(ctx)} />;
    },
    filterType: 'enum',
    zodBase(field: ValidatableField) {
      const items = resolveEnumItems(field);
      if (items.length === 0) return undefined;
      return z.custom(
        (value) =>
          (typeof value === 'string' || typeof value === 'number') &&
          items.some((item) => String(item.value) === String(value)),
        { error: 'rk.resource.validation.invalidEnum' },
      );
    },
  },

  image: {
    Cell<T>(ctx: CellContext<T>) {
      return <ImageDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value }: CsvContext<T>) {
      return stringifyRaw(value);
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <TextInput {...inputProps(ctx)} />;
    },
    filterType: 'string',
    zodBase: () => undefined,
  },

  json: {
    Cell<T>(ctx: CellContext<T>) {
      return <JsonDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value }: CsvContext<T>) {
      return stringifyRaw(value);
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <JsonInput {...inputProps(ctx)} />;
    },
    filterType: 'json',
    zodBase: () => z.custom(isJsonFormValue, { error: 'rk.resource.validation.invalidJson' }),
  },

  array: {
    Cell<T>(ctx: CellContext<T>) {
      return <ArrayDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value }: CsvContext<T>) {
      return stringifyRaw(value);
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <JsonInput {...inputProps(ctx)} />;
    },
    filterType: 'json',
    zodBase: () => undefined,
  },

  id: {
    Cell<T>(ctx: CellContext<T>) {
      return <IdDisplay {...displayProps(ctx)} />;
    },
    csvText<T>({ value }: CsvContext<T>) {
      const p = asPrimitive(value);
      return p === null || p === undefined || p === '' ? '' : String(p);
    },
    Input<T>(ctx: FormInputContext<T>) {
      return <TextInput {...inputProps(ctx)} />;
    },
    filterType: 'id',
    zodBase: () => undefined,
  },
} as const satisfies Record<FieldType, FieldTypeSpec>;

// ─── Level 3 of the widget resolution order (#1805) ────────
/**
 * The two component halves of each field type's default widget.
 *
 * Written out rather than derived from `FIELD_TYPE_REGISTRY` because the
 * legacy `Cell` / `Input` members are ADAPTERS onto these — deriving in that
 * direction would wrap the adapter in a second adapter and lose the component
 * identity React needs to keep a control mounted across renders.
 */
const TYPE_WIDGET_COMPONENTS = {
  text: { display: TextDisplay, input: TextInput },
  number: { display: NumberDisplay, input: NumberInput },
  currency: { display: CurrencyDisplay, input: CurrencyInput },
  date: { display: DateDisplay, input: DateInput },
  datetime: { display: DateTimeDisplay, input: DateTimeInput },
  boolean: { display: BooleanDisplay, input: BooleanInput },
  email: { display: EmailDisplay, input: EmailInput },
  phone: { display: PhoneDisplay, input: PhoneInput },
  enum: { display: EnumDisplay, input: EnumInput },
  image: { display: ImageDisplay, input: TextInput },
  json: { display: JsonDisplay, input: JsonInput },
  array: { display: ArrayDisplay, input: JsonInput },
  id: { display: IdDisplay, input: TextInput },
} as const satisfies Record<
  FieldType,
  {
    display: ComponentType<TypeWidgetProps>;
    input: ComponentType<TypeWidgetInputProps>;
  }
>;

/**
 * The closed per-`FieldType` widget set — level 3 of the resolution order
 * (`item.widget` → `field.widget` → this).
 *
 * `csvText` and `zodBase` are deliberately ABSENT, and #1805 slice S5 did NOT
 * change that even though it widened `Widget.csvText` into a real carrier.
 *
 * The reason is no longer "no honest value exists" — `WidgetCsvContext` now
 * carries the field and the live translator, so these rows COULD declare one.
 * It is that declaring it here would buy nothing and cost a fork: the type
 * projection would then exist twice, once in the row above and once in a
 * wrapper, and `formatFieldTextForExport` would have to pick. Level 3 stays the
 * `FIELD_TYPE_REGISTRY` row — the export consults the resolved widget first and
 * falls through to that row when the widget declares no `csvText`, which is
 * exactly what an absent member here means. `zodBase` is untouched and still
 * has no reader.
 */
export const FIELD_TYPE_WIDGETS: ReadonlyMap<FieldType, Widget> = new Map(
  (Object.keys(TYPE_WIDGET_COMPONENTS) as FieldType[]).map((type): [FieldType, Widget] => [
    type,
    {
      display: TYPE_WIDGET_COMPONENTS[type].display,
      input: TYPE_WIDGET_COMPONENTS[type].input,
      filterType: FIELD_TYPE_REGISTRY[type].filterType,
      supportedTypes: [type],
      modes: ['list', 'form'],
    },
  ]),
);

export { stringifyRaw };
